import { Suspense, useEffect, useMemo, useRef, useState } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowLeft } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from "@/components/ui/dialog"
import { FormField } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { toast } from "@/hooks/useToast"
import { Doc2UFormData, Doc2USchema } from "@/modules/patients/detail/progress-note/doc2u/schema/doc2u-schema"
import { useCreateDoc2USessionMutation } from "@/modules/patients/detail/progress-note/doc2u/services/useCreateDoc2USessionMutation"
import { useDoc2URecipientsQuery } from "@/modules/patients/detail/progress-note/doc2u/services/useDoc2URecipientsQuery"
import { useOpenNewWindow } from "@/modules/patients/detail/progress-note/hooks/useOpenNewWindow"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"

type Recipient = {
  label: string
  email: string
}

type Doc2UDialogProps = {
  onOpenChange: (open: boolean) => void
  onUpload: (url: string) => void
}

const Doc2URecipientsContent = ({
  recipients,
  isMailPopoverOpen,
  setIsMailPopoverOpen,
  onChange,
}: {
  recipients: Recipient[]
  isMailPopoverOpen: boolean
  setIsMailPopoverOpen: (open: boolean) => void
  onChange: (value: string) => void
}) => {
  const { t } = useTranslation()

  const sortedRecipients = useMemo(() => {
    return [...recipients].sort((a, b) => {
      const aLabel = a.label === "MEAS" ? "" : a.label
      const bLabel = b.label === "MEAS" ? "" : b.label
      return aLabel.localeCompare(bLabel)
    })
  }, [recipients])

  return (
    <Popover modal open={isMailPopoverOpen} onOpenChange={setIsMailPopoverOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="tertiary"
          className="text-on-surface-subdued absolute top-1 right-1 px-2"
          data-testid="open-email-list-popover">
          <ArrowLeft />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <Command
          className="bg-surface *:[[cmdk-input-wrapper]]:border-b *:[[cmdk-input-wrapper]]:p-2"
          filter={(email, keyword) => {
            const recipientText = recipients.find((value) => value.email === email)?.label
            const lowerEmail = email.toLowerCase()
            const lowerKeyword = keyword.toLowerCase()
            const lowerRecipientText = recipientText?.toLowerCase()

            const isMatch = lowerEmail.includes(lowerKeyword) || lowerRecipientText?.includes(lowerKeyword)

            return isMatch ? 1 : 0
          }}>
          <CommandInput placeholder={t("episodeOfCare.progressNote.doc2u.searchEmail")} />
          <CommandList className="bg-surface py-2">
            <CommandEmpty className="px-2 py-1.5 text-sm">
              {t("episodeOfCare.progressNote.doc2u.noEmailFound")}
            </CommandEmpty>
            <CommandGroup>
              {sortedRecipients.map((recipient) => (
                <CommandItem
                  data-testid="pharmacy-email"
                  key={recipient.email}
                  value={recipient.email}
                  className="cursor-pointer flex-wrap gap-x-1"
                  onSelect={(value) => {
                    setIsMailPopoverOpen(false)
                    onChange(value)
                  }}>
                  {recipient.label}
                  <span>({recipient.email})</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const RecipientsContent = (props: {
  isMailPopoverOpen: boolean
  setIsMailPopoverOpen: (open: boolean) => void
  onChange: (value: string) => void
}) => {
  const { ch_doc2URecipients } = useDoc2URecipientsQuery()

  const recipients: Recipient[] = ch_doc2URecipients.map((r) => ({
    label: r.userName ?? r.email,
    email: r.email,
  }))

  return <Doc2URecipientsContent recipients={recipients} {...props} />
}

export const Doc2UDialog = ({ onUpload, onOpenChange }: Doc2UDialogProps) => {
  const { t } = useTranslation()
  const openNewWindow = useOpenNewWindow()
  const targetPage = useRef<Window | null>(null)
  const appointmentId = useAppointmentParam()
  const contextAppointment = useContextAppointment()
  const [createDoc2USession, isCreatingSession] = useCreateDoc2USessionMutation()
  const [doc2UBaseUrl, setDoc2UBaseUrl] = useState("")
  const [isMailPopoverOpen, setIsMailPopoverOpen] = useState(false)

  const form = useForm<Doc2UFormData>({
    defaultValues: {
      email: contextAppointment?.partnerUserEmail ?? "",
    },
    resolver: zodResolver(Doc2USchema),
  })

  const {
    control,
    reset,
    formState: { errors },
    handleSubmit,
  } = form

  const openDoc2U = (roomToken: string, baseUrl: string) => {
    targetPage.current?.close()

    targetPage.current = openNewWindow(
      `${baseUrl}/room?t=${roomToken}&disableReportDownload=true&practitionerMode=remote`,
      "doc2u",
      false,
    )
  }

  const handleStartingDoc2U: SubmitHandler<Doc2UFormData> = (data) => {
    if (appointmentId) {
      createDoc2USession({
        variables: {
          input: {
            appointmentId,
            emailContact: data.email,
          },
        },
        onCompleted({ createDoc2USession: { roomDoc2UOutput } }) {
          if (roomDoc2UOutput) {
            setDoc2UBaseUrl(roomDoc2UOutput.appEndpoint)
            openDoc2U(roomDoc2UOutput.practitionerTokenKey, roomDoc2UOutput.appEndpoint)
            reset()
          }
        },
        onError() {
          toast({ description: t("common.somethingWentWrong") })
        },
      })
    }
  }

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin === doc2UBaseUrl) {
        if (event.data.service === "app" && event.data.event === "status" && event.data.status === "ready") {
          if (targetPage.current && !targetPage.current.closed) {
            targetPage.current.postMessage({ service: "app", event: "enable_post_message" }, doc2UBaseUrl)
          }
        }

        if (event.data.service === "report" && event.data.event === "pdf" && event.data.dataURL) {
          onUpload(event.data.dataURL)
          onOpenChange(false)
        }
      }
    }

    if (doc2UBaseUrl) window.addEventListener("message", handleMessage)

    return () => window.removeEventListener("message", handleMessage)
  }, [doc2UBaseUrl, onUpload, onOpenChange])

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay onMouseDown={(e) => e.stopPropagation()} />
        <DialogContent
          data-testid="doc2u-dialog-content"
          className="flex max-h-full flex-col"
          variant="small"
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader data-testid="doc2u-dialog-header">
            <DialogTitle>{t("episodeOfCare.progressNote.doc2u.startDoc2uSession")}</DialogTitle>
          </DialogHeader>
          <FormProvider {...form}>
            <div className="px-6">
              <p className="text-on-surface-subdued pb-4 text-sm">
                {t("episodeOfCare.progressNote.doc2u.pleaseSelectDoc2UInvitation")}
              </p>
              <div className="relative">
                <FormField
                  name="email"
                  control={control}
                  render={({ field: { ref, value, onChange } }) => {
                    return (
                      <>
                        <Input
                          ref={ref}
                          label={t("episodeOfCare.progressNote.doc2u.emailAddress")}
                          required
                          translateError
                          value={value}
                          error={errors.email?.message}
                          onChange={onChange}
                          className="pr-10"
                        />
                        <Suspense fallback={null}>
                          <RecipientsContent
                            isMailPopoverOpen={isMailPopoverOpen}
                            setIsMailPopoverOpen={setIsMailPopoverOpen}
                            onChange={onChange}
                          />
                        </Suspense>
                      </>
                    )
                  }}
                />
              </div>
            </div>
          </FormProvider>
          <DialogFooter className="mt-auto" data-testid="doc2u-dialog-footer">
            <Button type="button" variant="secondary" data-testid="cancel-button" onClick={() => onOpenChange(false)}>
              {t("common.button.cancel")}
            </Button>
            <Button data-testid="send-button" isLoading={isCreatingSession} onClick={handleSubmit(handleStartingDoc2U)}>
              {t("common.button.send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
